/**
 * Keyboard shortcuts composable for Dev Panel
 * Provides global keyboard shortcuts for development tools
 */
import { onMounted, onUnmounted } from "vue";
import { useDevPanel } from "./useDevPanel";
import { useDevSettings } from "./useDevSettings";
import { useEventLog } from "./useEventLog";
import { isLocalEnv } from "@/utils/IdentifyGlobalEnv";

interface ShortcutDefinition {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  action: () => void;
}

export function useKeyboardShortcuts() {
  const { togglePanel, openPanel, minimizePanel } = useDevPanel();
  const { resetSettings, exportSettings } = useDevSettings();
  const { exportEvents, clearEvents } = useEventLog();

  // Define shortcuts
  const shortcuts: ShortcutDefinition[] = [
    {
      key: "D",
      ctrl: true,
      shift: true,
      description: "Toggle Dev Panel",
      action: togglePanel,
    },
    {
      key: "R",
      ctrl: true,
      shift: true,
      alt: true,
      description: "Reset All Dev Settings",
      action: () => {
        resetSettings();
        console.log("[DevPanel] Settings reset to defaults");
      },
    },
    {
      key: "E",
      ctrl: true,
      shift: true,
      alt: true,
      description: "Export Events to JSON",
      action: () => {
        const eventsJson = exportEvents();
        const blob = new Blob([eventsJson], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ai-assistant-events-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
        console.log("[DevPanel] Events exported");
      },
    },
    {
      key: "Escape",
      description: "Minimize Dev Panel",
      action: minimizePanel,
    },
  ];

  const handleKeyDown = (e: KeyboardEvent) => {
    // Only handle shortcuts in local environment
    if (!isLocalEnv()) return;

    for (const shortcut of shortcuts) {
      const keyMatch = e.key.toUpperCase() === shortcut.key.toUpperCase() || e.key === shortcut.key;
      const ctrlMatch = shortcut.ctrl ? e.ctrlKey : !e.ctrlKey;
      const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;
      const altMatch = shortcut.alt ? e.altKey : !e.altKey;

      // Special case for Escape - no modifiers required
      if (shortcut.key === "Escape" && e.key === "Escape") {
        e.preventDefault();
        shortcut.action();
        return;
      }

      if (keyMatch && ctrlMatch && shiftMatch && altMatch) {
        e.preventDefault();
        shortcut.action();
        return;
      }
    }
  };

  const registerShortcuts = () => {
    document.addEventListener("keydown", handleKeyDown);
  };

  const unregisterShortcuts = () => {
    document.removeEventListener("keydown", handleKeyDown);
  };

  // Auto-register in component context
  onMounted(() => {
    registerShortcuts();
  });

  onUnmounted(() => {
    unregisterShortcuts();
  });

  return {
    shortcuts,
    registerShortcuts,
    unregisterShortcuts,
  };
}

/**
 * Format shortcut for display
 */
export function formatShortcut(shortcut: { key: string; ctrl?: boolean; shift?: boolean; alt?: boolean }): string {
  const parts: string[] = [];
  if (shortcut.ctrl) parts.push("Ctrl");
  if (shortcut.shift) parts.push("Shift");
  if (shortcut.alt) parts.push("Alt");
  parts.push(shortcut.key);
  return parts.join("+");
}


